// ProfileStats — Total creations, FLZ earned, Level, Badges
import { Award, Image, Layers, Star, TrendingUp, Video, Zap } from 'lucide-react'

export interface ProfileStatsData {
  total_images: number
  total_videos: number
  total_lipsync: number
  total_web: number
  flz_earned: number
  level: number
  xp: number
  badges: string[]
}

interface Props {
  stats: ProfileStatsData | null
  loading?: boolean
}

const BADGE_COLORS: Record<string, { bg: string; color: string }> = {
  creator: { bg: 'rgba(102,126,234,0.12)', color: '#667eea' },
  prolific: { bg: 'rgba(245,158,11,0.12)', color: '#f59e0b' },
  pioneer: { bg: 'rgba(16,185,129,0.12)', color: '#10b981' },
  master: { bg: 'rgba(236,72,153,0.12)', color: '#ec4899' },
}

export default function ProfileStats({ stats, loading }: Props) {
  if (loading || !stats) {
    return <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary, #8b949e)', fontSize: 13 }}>Loading stats...</div>
  }

  const statCards = [
    { icon: <Image size={18} />, value: stats.total_images, label: 'Images', color: '#667eea' },
    { icon: <Video size={18} />, value: stats.total_videos, label: 'Videos', color: '#8b5cf6' },
    { icon: <Layers size={18} />, value: stats.total_lipsync, label: 'LipSync', color: '#ec4899' },
    { icon: <Zap size={18} />, value: stats.flz_earned, label: 'FLZ Earned', color: '#f59e0b' },
  ]

  return (
    <div>
      {/* Level bar */}
      <div style={{
        padding: 20, borderRadius: 14, marginBottom: 20,
        background: 'linear-gradient(135deg, rgba(102,126,234,0.08), rgba(118,75,162,0.08))',
        border: '1px solid var(--border-default, #30363d)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Star size={18} color="#f59e0b" />
            <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary, #e6edf3)' }}>Level {stats.level}</span>
          </div>
          <span style={{ fontSize: 12, color: 'var(--text-secondary, #8b949e)' }}>{stats.xp % 1000} / 1000 XP</span>
        </div>
        <div style={{ height: 8, borderRadius: 10, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
          <div style={{
            width: `${Math.min(100, (stats.xp % 1000) / 10)}%`, height: '100%',
            background: 'linear-gradient(90deg, #667eea, #a855f7, #f093fb)',
            borderRadius: 10, transition: 'width 1s ease',
          }} />
        </div>
      </div>

      {/* Stat cards grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
        {statCards.map(s => (
          <div key={s.label} style={{
            padding: 16, borderRadius: 12, textAlign: 'center',
            border: '1px solid var(--border-default, #30363d)',
            background: 'rgba(255,255,255,0.02)',
          }}>
            <div style={{ color: s.color, marginBottom: 8, display: 'flex', justifyContent: 'center' }}>{s.icon}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary, #e6edf3)' }}>{s.value}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary, #8b949e)', marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Badges */}
      {stats.badges.length > 0 && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
            <Award size={15} color="#f59e0b" />
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary, #e6edf3)' }}>Badges</span>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {stats.badges.map(badge => {
              const style = BADGE_COLORS[badge] || { bg: 'rgba(148,163,184,0.12)', color: '#94a3b8' }
              return (
                <span key={badge} style={{
                  display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px',
                  borderRadius: 8, background: style.bg, color: style.color,
                  fontSize: 12, fontWeight: 700,
                }}>
                  <TrendingUp size={12} /> {badge}
                </span>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
